/**
 * Filter Panel Component
 * Comprehensive filtering controls for relationships
 */

import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Slider,
  Button,
  Divider,
  Chip,
  TextField,
} from '@mui/material';
import {
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import useTokenExaminerStore from '../stores/tokenExaminerStore';
import { getFaanaHealthColor, getFaanaZoneColor } from '../utils/faanaScoring';

const RELATIONSHIP_TYPES = [
  'EMPLOYMENT', 'LEADERSHIP', 'MEMBERSHIP', 'PARTNERSHIP', 'FUNDING',
  'OWNERSHIP', 'PARTICIPATION', 'MANAGEMENT', 'AFFILIATION', 'COLLABORATION',
  'VENDOR', 'CLIENT', 'ADVISORY', 'INVESTMENT', 'MENTORSHIP', 'TEAM_MEMBER',
  'PEER', 'COMPETITOR', 'SUPPORTER', 'OTHER'
];

const CONFIDENCE_LEVELS = ['HIGH', 'MEDIUM', 'LOW'];

const BEHAVIORAL_ZONES = ['learning', 'comfort', 'panic'];

const ENTITY_TYPES = ['PERSON', 'ORGANIZATION', 'PROJECT', 'PROGRAM', 'EVENT', 'RESOURCE'];

// eslint-disable-next-line max-lines-per-function
function FilterPanel() {
  const {
    filters,
    relationships,
    setFilters,
    clearFilters,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  const filteredCount = getFilteredRelationships().length;
  const totalCount = relationships.length;

  const handleRelationshipTypesChange = (event) => {
    const value = event.target.value;
    setFilters({
      relationshipTypes: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleConfidenceChange = (event) => {
    const value = event.target.value;
    setFilters({
      confidenceLevels: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleHealthRangeChange = (event, newValue) => {
    setFilters({
      healthRange: newValue,
    });
  };

  const handleConfirmationStatusChange = (event) => {
    setFilters({
      confirmationStatus: event.target.value,
    });
  };

  const handleEntityTypesChange = (event) => {
    const value = event.target.value;
    setFilters({
      entityTypes: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleBehavioralZonesChange = (event) => {
    const value = event.target.value;
    setFilters({
      behavioralZones: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleDateRangeChange = (field, date) => {
    const currentRange = filters.dateRange || [null, null];
    const newRange = [...currentRange];
    newRange[field === 'start' ? 0 : 1] = date;
    setFilters({
      dateRange: newRange,
    });
  };

  const handleClearFilters = () => {
    clearFilters();
  };

  const activeFilterCount = Object.keys(filters).filter(key => {
    const value = filters[key];
    if (Array.isArray(value)) return value.length > 0;
    if (value === null || value === undefined) return false;
    if (key === 'confirmationStatus') return value !== 'all';
    return true;
  }).length;

  return (
    <Box sx={{ width: '100%', maxWidth: 400 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon />
          Filters
          {activeFilterCount > 0 && (
            <Chip
              label={activeFilterCount}
              size="small"
              color="primary"
            />
          )}
        </Typography>
        <Button
          size="small"
          startIcon={<ClearIcon />}
          onClick={handleClearFilters}
          disabled={activeFilterCount === 0}
        >
          Clear
        </Button>
      </Box>

      {/* Results Count */}
      <Box sx={{ mb: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredCount} of {totalCount} relationships
        </Typography>
      </Box>

      {/* Relationship Types */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Relationship Types</InputLabel>
        <Select
          multiple
          value={filters.relationshipTypes || []}
          onChange={handleRelationshipTypesChange}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {RELATIONSHIP_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              <Checkbox checked={(filters.relationshipTypes || []).indexOf(type) > -1} />
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Confidence Levels */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Confidence Levels</InputLabel>
        <Select
          multiple
          value={filters.confidenceLevels || []}
          onChange={handleConfidenceChange}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {CONFIDENCE_LEVELS.map((level) => (
            <MenuItem key={level} value={level}>
              <Checkbox checked={(filters.confidenceLevels || []).indexOf(level) > -1} />
              {level}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Health Range */}
      <Box sx={{ mb: 2 }}>
        <Typography gutterBottom>
          Health Score Range: {filters.healthRange?.[0] || 0} - {filters.healthRange?.[1] || 100}
        </Typography>
        <Slider
          value={filters.healthRange || [0, 100]}
          onChange={handleHealthRangeChange}
          valueLabelDisplay="auto"
          min={0}
          max={100}
          marks={[
            { value: 0, label: 'Critical' },
            { value: 40, label: 'Concerning' },
            { value: 60, label: 'Healthy' },
            { value: 80, label: 'Thriving' },
            { value: 100, label: '100' },
          ]}
          sx={{
            '& .MuiSlider-track': {
              background: `linear-gradient(to right, ${getFaanaHealthColor(0)}, ${getFaanaHealthColor(40)}, ${getFaanaHealthColor(60)}, ${getFaanaHealthColor(80)}, ${getFaanaHealthColor(100)})`,
            },
          }}
        />
      </Box>

      {/* Confirmation Status */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Confirmation Status</InputLabel>
        <Select
          value={filters.confirmationStatus || 'all'}
          onChange={handleConfirmationStatusChange}
        >
          <MenuItem value="all">All Relationships</MenuItem>
          <MenuItem value="confirmed">Confirmed Only</MenuItem>
          <MenuItem value="unconfirmed">Unconfirmed Only</MenuItem>
        </Select>
      </FormControl>

      <Divider sx={{ my: 2 }} />

      {/* Entity Types */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Entity Types</InputLabel>
        <Select
          multiple
          value={filters.entityTypes || []}
          onChange={handleEntityTypesChange}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {ENTITY_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              <Checkbox checked={(filters.entityTypes || []).indexOf(type) > -1} />
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Behavioral Zones */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Behavioral Zones</InputLabel>
        <Select
          multiple
          value={filters.behavioralZones || []}
          onChange={handleBehavioralZonesChange}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  size="small"
                  sx={{ backgroundColor: getFaanaZoneColor(value), color: 'white' }}
                />
              ))}
            </Box>
          )}
        >
          {BEHAVIORAL_ZONES.map((zone) => (
            <MenuItem key={zone} value={zone}>
              <Checkbox checked={(filters.behavioralZones || []).indexOf(zone) > -1} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: getFaanaZoneColor(zone),
                  }}
                />
                {zone}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ my: 2 }} />

      {/* Date Range */}
      <Typography variant="subtitle2" gutterBottom>
        Discovery Date Range
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={filters.dateRange?.[0] ? filters.dateRange[0].toISOString().split('T')[0] : ''}
          onChange={(e) => handleDateRangeChange('start', e.target.value ? new Date(e.target.value) : null)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={filters.dateRange?.[1] ? filters.dateRange[1].toISOString().split('T')[0] : ''}
          onChange={(e) => handleDateRangeChange('end', e.target.value ? new Date(e.target.value) : null)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* Quick Filter Presets */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Filters
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setFilters({
              healthRange: [0, 40],
              confirmationStatus: 'unconfirmed'
            })}
          >
            Critical Unconfirmed
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setFilters({
              confidenceLevels: ['HIGH'],
              confirmationStatus: 'unconfirmed'
            })}
          >
            High Confidence Pending
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setFilters({
              healthRange: [80, 100],
              confirmationStatus: 'confirmed'
            })}
          >
            Thriving Relationships
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setFilters({
              relationshipTypes: ['MENTORSHIP', 'LEADERSHIP', 'ADVISORY']
            })}
          >
            Development Relationships
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default FilterPanel;